import React, { DragEventHandler } from "react";
import { FormattedMessage } from "react-intl";
import styled from "styled-components";
import {
    COLORS,
    TYPE_INFO,
    CLR_BLACK,
    CLR_GREY,
    CLR_LIGHTGREY,
    CLR_TRANSPARENT,
} from "./Colors";
import { device } from "../../helpers/style";
import { addClass, fixArabicText, removeClass } from "../../helpers";

const { color, backgroundColor } = COLORS[TYPE_INFO];

const Container = styled.table`
    user-select: none;
    border-collapse: collapse;
    width: 100%;
`;

const Heading = styled.th`
    padding: 0 20px 5px 0;
    border-bottom: 2px solid ${CLR_GREY};
    font-size: 20px;

    span {
        font-weight: 500;
        font-family: Cairo, QuranFont, urdu, sans-serif;
    }

    @media ${device.phone} {
        padding: 0 10px 8px 0px;
    } ;
`;

const Head = styled.thead`
    text-align: right;
`;
const HeadRow = styled.tr``;

interface TBodyProps {
    onDragOver?: DragEventHandler<HTMLElement>;
    onDragEnter?: DragEventHandler<HTMLElement>;
    onDragLeave?: DragEventHandler<HTMLElement>;
    onDragEnd?: DragEventHandler<HTMLElement>;
}
const TBody = styled.tbody<TBodyProps>``;

interface BodyRowProps {
    index?: number;
    selected: boolean;
    draggable?: boolean;
}
const BodyRow = styled.tr.attrs({
    index: (p: BodyRowProps) => p.index,
})<BodyRowProps>`
    position: relative;
    color: ${(p) => (p.selected ? color : CLR_BLACK)};
    background-color: ${(p) =>
        p.selected ? backgroundColor : CLR_TRANSPARENT};

    &:nth-child(2n) {
        background-color: ${(p) => (p.selected ? backgroundColor : "#f5f5f5")};
    }

    &:hover {
        background-color: ${(p) =>
            p.selected ? backgroundColor : CLR_LIGHTGREY};
    }

    &.drop {
        cursor: move;
    }

    &.drop * {
        background-color: ${backgroundColor};
    }

    & td:first-of-type:before {
        position: absolute;
        content: " ";
        top: calc(50% - 8px);
        right: -24px;
        width: 0;
        height: 0;
        border-top: 8px solid transparent;
        border-bottom: 8px solid transparent;
        border-right: 8px solid transparent;
    }

    &:hover td:first-of-type:before {
        border-right-color: ${CLR_GREY};
    }
`;

interface ItemProps {
    paddingRight?: number;
    isFocused?: boolean;
}
const Item = styled.td<ItemProps>`
    position: relative;
    border-bottom: 1px solid #e6e6e6;
    padding: 10px 20px;
    padding-right: ${(p) => (p.paddingRight ? p.paddingRight * 50 : 20)}px;
    font-size: 18px;
    font-weight: ${(p) => (p.isFocused ? 700 : 500)};

    [lang="ar"] {
        font-size: 16px;
    }

    @media ${device.phone} {
        font-size: 15px;
        padding: 6px 10px 6px 0;
        padding-right: ${(p) => (p.paddingRight ? p.paddingRight * 30 : 20)}px;
    }
`;

export type RowProps = Record<string, string | number>;
export type RowPositions = Array<{ position: number; id: number }>;
interface TableProps {
    fields: string[];
    titles: string[];
    rows: Array<RowProps>;
    indent?: boolean;
    transform?: Record<string, (value: any) => string>;
    focusedRowIndex?: number;
    width: string[];
    draggable?: boolean;
    rowId?: string;
    isSelected?: (row: RowProps) => boolean;
    onRowClick: (row: RowProps) => void;
    onDragEnd?: (positions: RowPositions) => void;
}
type DraggedElement = HTMLElement | null;
export class Table extends React.Component<TableProps> {
    private dragged: DraggedElement;

    constructor(props: TableProps) {
        super(props);
        this.dragged = null;
    }

    head() {
        const { titles } = this.props;
        return (
            <Head>
                <HeadRow>
                    {titles.map((messageId: string) => (
                        <Heading key={`title-${messageId}`}>
                            <FormattedMessage id={messageId} />
                        </Heading>
                    ))}
                </HeadRow>
            </Head>
        );
    }

    col(
        field: string,
        value: string | number,
        rowNum: number,
        colNum: number,
        row: RowProps
    ) {
        const { indent, transform, focusedRowIndex } = this.props;
        let v = value;

        if (
            transform &&
            transform[field] &&
            typeof transform[field] === "function"
        ) {
            v = transform[field](value);
        }

        if (colNum === 0 && indent) {
            return (
                <Item
                    paddingRight={row._indent as number}
                    key={`col-${rowNum}-${colNum}`}
                    isFocused={focusedRowIndex === rowNum}
                    dangerouslySetInnerHTML={fixArabicText(v.toString())}
                />
            );
        }

        return (
            <Item
                key={`col-${rowNum}-${colNum}`}
                isFocused={focusedRowIndex === rowNum}
                dangerouslySetInnerHTML={fixArabicText(v.toString())}
            />
        );
    }

    getTR(evt: React.DragEvent<HTMLElement>): DraggedElement {
        const node = evt.target as HTMLElement;
        if (!node) {
            return null;
        }

        if (node.nodeName.toLowerCase() === "tr") {
            return node as HTMLElement;
        }

        return node.parentNode as HTMLElement;
    }

    onDragStart = (evt: React.DragEvent<HTMLElement>) => {
        if (evt.dataTransfer) {
            evt.dataTransfer.effectAllowed = "move";
            this.dragged = this.getTR(evt);
        }
    };

    onDragOver = (evt: React.DragEvent<HTMLElement>) => {
        evt.preventDefault();

        const target = this.getTR(evt);
        addClass(target as HTMLElement, "drop");
    };

    onDragEnter = (evt: React.DragEvent<HTMLElement>) => {
        evt.preventDefault();
    };

    onDragLeave = (evt: React.DragEvent<HTMLElement>) => {
        const target = this.getTR(evt);
        removeClass(target as HTMLElement, "drop");
    };

    onDrop = (evt: React.DragEvent<HTMLElement>) => {
        if (!this.dragged) {
            return;
        }

        const d = this.dragged;
        const parent = (d.nodeName.toLowerCase() === "tr"
            ? d.parentNode
            : d) as HTMLElement;

        const target = this.getTR(evt);
        if (!target) {
            return;
        }

        removeClass(target as HTMLElement, "drop");
        if (target.nextSibling === null) {
            parent.insertBefore(this.dragged, target.nextSibling);
        } else {
            console.log("this.dragged", this.dragged, "target", target);
            if (this.dragged.nextSibling === target) {
                parent.insertBefore(this.dragged, target.nextSibling);
            } else {
                parent.insertBefore(this.dragged, target);
            }
        }

        const rows = parent.querySelectorAll("tr");

        let positions: RowPositions = [];
        for (let i = 0; i < rows.length; i += 1) {
            positions.push({
                position: i + 1,
                id: parseInt(rows[i].dataset.index!),
            });
        }

        if (this.props.onDragEnd) {
            this.props.onDragEnd(positions);
        }
    };

    onDragEnd = (_evt: React.DragEvent<HTMLElement>) => {
        this.dragged = null;
    };

    row(row: RowProps, rowNum: number) {
        const { fields, isSelected, onRowClick, draggable, rowId } = this.props;
        const selected = isSelected ? isSelected(row) : false;

        if (draggable) {
            const index = rowId ? row[rowId] : undefined;
            return (
                <BodyRow
                    key={`row-${rowNum}`}
                    data-index={index}
                    onClick={() => onRowClick(row)}
                    selected={selected}
                    draggable={draggable}
                    onDragStart={this.onDragStart}
                    onDrop={this.onDrop}
                >
                    {fields.map((field, j: number) =>
                        this.col(field, row[field], rowNum, j, row)
                    )}
                </BodyRow>
            );
        }

        return (
            <BodyRow
                key={`row-${rowNum}`}
                selected={selected}
                onClick={() => onRowClick(row)}
            >
                {fields.map((field, j: number) =>
                    this.col(field, row[field], rowNum, j, row)
                )}
            </BodyRow>
        );
    }

    body() {
        const { rows, draggable } = this.props;

        if (draggable) {
            return (
                <TBody
                    onDragOver={this.onDragOver}
                    onDragEnter={this.onDragEnter}
                    onDragLeave={this.onDragLeave}
                    onDragEnd={this.onDragEnd}
                >
                    {rows.map((row, i: number) => this.row(row, i))}
                </TBody>
            );
        }

        return <TBody>{rows.map((row, i: number) => this.row(row, i))}</TBody>;
    }

    width() {
        return (
            <colgroup>
                {this.props.width.map((w: string, i: number) => (
                    <col key={`cl-w-${i}`} width={w} />
                ))}
            </colgroup>
        );
    }

    render() {
        return (
            <Container>
                {this.width()}
                {this.head()}
                {this.body()}
            </Container>
        );
    }
}
